import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Badge,
  SwipeableDrawer,
  Divider,
  Chip,
  Button,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  School as CoachingIcon,
  People as FriendIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { Notification } from '../hooks/useNotifications';
import { useNotificationsContext } from '../contexts/NotificationsContext';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

interface NotificationCenterProps {
  open: boolean;
  onClose: () => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    notifications,
    unreadCount,
    loading,
    hasPermission,
    hasSupportForNotifications,
    requestPermission,
    unsubscribe,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    sendTestNotification,
    deleteNotification,
    clearAllNotifications,
  } = useNotificationsContext();

  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [enablingNotifications, setEnablingNotifications] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info'>('success');
  const [swipeStates, setSwipeStates] = useState<Record<string, number>>({});
  const [clearingAll, setClearingAll] = useState(false);

  useEffect(() => {
    if (open) {
      fetchNotifications(50, showUnreadOnly);
    }
  }, [open, showUnreadOnly, fetchNotifications]);

  const getNotificationIcon = (notification: Notification) => {
    if (notification.type === 'coaching') return <CoachingIcon sx={{ color: theme.palette.info.main }} />;
    if (notification.data?.type === 'friend_post') return <FriendIcon sx={{ color: '#CE93D8' }} />;
    if (notification.data?.type === 'friend_message') return <FriendIcon sx={{ color: '#CE93D8' }} />;
    if (notification.data?.type === 'friend_request') return <FriendIcon sx={{ color: '#00E0FF' }} />;
    if (notification.type === 'friend_activity') return <FriendIcon sx={{ color: '#00E0FF' }} />;
    return <InfoIcon color="action" />;
  };

  const getNotificationColor = (notification: Notification) => {
    if (notification.type === 'coaching') return theme.palette.info.main;
    if (notification.data?.type === 'friend_post') return '#CE93D8';
    if (notification.data?.type === 'friend_message') return '#CE93D8';
    if (notification.data?.type === 'friend_request') return '#00E0FF';
    if (notification.type === 'friend_activity') return '#00E0FF';
    return theme.palette.text.secondary;
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      markAsRead(notification._id);
    }
    if (notification.data?.url) {
      let url = notification.data.url;
      const postId = notification.data.postId;
      if (postId && !url.includes('postId=')) {
        url += `${url.includes('?') ? '&' : '?'}postId=${postId}`;
      }
      onClose();
      setTimeout(() => {
        navigate(url);
      }, 350);
    }
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
    fetchNotifications(50, showUnreadOnly);
  };

  const handleEnableNotifications = async () => {
    setEnablingNotifications(true);
    try {
      const success = await requestPermission();
      if (success) {
        setSnackbarMessage('Notifications enabled successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage('Failed to enable notifications. Please check browser permissions.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage('Error enabling notifications. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setEnablingNotifications(false);
    }
  };

  const handleDisableNotifications = async () => {
    setEnablingNotifications(true);
    try {
      await unsubscribe();
      setSnackbarMessage('Notifications disabled successfully.');
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage('Error disabling notifications.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setEnablingNotifications(false);
    }
  };

  const handleSendTest = async () => {
    setSendingTest(true);
    try {
      const success = await sendTestNotification();
      if (success) {
        setSnackbarMessage('Test notification sent! Check your notifications.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage('Failed to send test notification. Please make sure notifications are enabled.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage('Error sending test notification.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSendingTest(false);
    }
  };

  const handleDeleteNotification = async (notificationId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const success = await deleteNotification(notificationId);
    if (success) {
      setSnackbarMessage('Notification deleted');
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
    }
  };

  const handleClearAll = async () => {
    setClearingAll(true);
    try {
      const success = await clearAllNotifications();
      if (success) {
        setSnackbarMessage('All notifications cleared');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage('Failed to clear notifications');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarMessage('Error clearing notifications');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setClearingAll(false);
    }
  };

  // Swipe handlers for touch devices
  const handleTouchStart = (notificationId: string, e: React.TouchEvent) => {
    setSwipeStates(prev => ({ ...prev, [notificationId]: e.touches[0].clientX }));
  };

  const handleTouchMove = (notificationId: string, e: React.TouchEvent) => {
    const startX = swipeStates[notificationId];
    if (startX === undefined) return;

    const currentX = e.touches[0].clientX;
    const diff = startX - currentX;

    // Update visual feedback (could add translateX here for visual swipe)
    if (diff > 0) {
      setSwipeStates(prev => ({ ...prev, [`${notificationId}_offset`]: Math.min(diff, 100) }));
    }
  };

  const handleTouchEnd = async (notificationId: string) => {
    const offset = swipeStates[`${notificationId}_offset`] || 0;

    // If swiped more than 80px, delete the notification
    if (offset > 80) {
      await deleteNotification(notificationId);
      setSnackbarMessage('Notification deleted');
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
    }

    // Reset swipe state
    setSwipeStates(prev => {
      const newState = { ...prev };
      delete newState[notificationId];
      delete newState[`${notificationId}_offset`];
      return newState;
    });
  };

  return (
    <SwipeableDrawer
      anchor="right"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      PaperProps={{
        sx: {
          width: isMobile ? 'calc(100% - 48px)' : 400,
          maxWidth: 400,
          background: 'var(--bg-card)',
          borderLeft: '1px solid rgba(0,224,255,0.15)',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsIcon />
            <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: 1 }}>
              Notifications
            </Typography>
            {unreadCount > 0 && (
              <Badge badgeContent={unreadCount} color="error" max={99} />
            )}
          </Box>
          <IconButton
            onClick={onClose}
            size="medium"
            aria-label="Close notifications"
            sx={{
              color: '#00E0FF',
              backgroundColor: 'rgba(0,224,255,0.1)',
              border: '1px solid rgba(0,224,255,0.2)',
              '&:hover': {
                backgroundColor: 'rgba(0,224,255,0.2)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Notification Settings */}
        {hasSupportForNotifications && (
          <Box
            sx={{
              p: 2,
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'var(--font-mono)' }}>
                Push Notifications
              </Typography>
              <Chip
                label={hasPermission ? 'Enabled' : 'Disabled'}
                color={hasPermission ? 'success' : 'default'}
                size="small"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {!hasPermission ? (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={handleEnableNotifications}
                  disabled={enablingNotifications}
                  fullWidth
                >
                  {enablingNotifications ? 'Enabling...' : 'Enable Notifications'}
                </Button>
              ) : (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleSendTest}
                    disabled={sendingTest}
                    sx={{ flex: 1 }}
                  >
                    {sendingTest ? 'Sending...' : 'Send Test'}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={handleDisableNotifications}
                    disabled={enablingNotifications}
                    sx={{ flex: 1 }}
                  >
                    {enablingNotifications ? 'Disabling...' : 'Disable'}
                  </Button>
                </>
              )}
            </Box>
          </Box>
        )}

        {/* Actions */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            gap: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Button
            size="small"
            variant={showUnreadOnly ? 'outlined' : 'contained'}
            onClick={() => setShowUnreadOnly(false)}
          >
            All
          </Button>
          <Button
            size="small"
            variant={showUnreadOnly ? 'contained' : 'outlined'}
            onClick={() => setShowUnreadOnly(true)}
          >
            Unread ({unreadCount})
          </Button>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            {unreadCount > 0 && (
              <Button
                size="small"
                onClick={handleMarkAllRead}
              >
                Mark read
              </Button>
            )}
            {notifications.length > 0 && (
              <Button
                size="small"
                color="error"
                onClick={handleClearAll}
                disabled={clearingAll}
              >
                {clearingAll ? 'Clearing...' : 'Clear all'}
              </Button>
            )}
          </Box>
        </Box>

        {/* Notification List */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: 4,
              }}
            >
              <CircularProgress />
            </Box>
          ) : notifications.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4,
                textAlign: 'center',
              }}
            >
              <NotificationsIcon
                sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.3, mb: 2 }}
              />
              <Typography variant="body1" color="text.secondary">
                {showUnreadOnly ? 'No unread notifications' : 'No notifications yet'}
              </Typography>
            </Box>
          ) : (
            <List>
              {notifications.map((notification, index) => (
                <React.Fragment key={notification._id}>
                  <ListItem
                    button
                    onClick={() => handleNotificationClick(notification)}
                    onTouchStart={(e) => handleTouchStart(notification._id, e)}
                    onTouchMove={(e) => handleTouchMove(notification._id, e)}
                    onTouchEnd={() => handleTouchEnd(notification._id)}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={(e) => handleDeleteNotification(notification._id, e)}
                        size="small"
                        sx={{
                          opacity: 0.6,
                          '&:hover': { opacity: 1, color: 'error.main' }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                    sx={{
                      backgroundColor: notification.read
                        ? 'transparent'
                        : theme.palette.action.hover,
                      '&:hover': {
                        backgroundColor: theme.palette.action.selected,
                      },
                      borderLeft: `4px solid ${getNotificationColor(notification)}`,
                      transform: `translateX(-${swipeStates[`${notification._id}_offset`] || 0}px)`,
                      transition: swipeStates[notification._id] ? 'none' : 'transform 0.2s ease-out',
                    }}
                  >
                    <ListItemIcon>{getNotificationIcon(notification)}</ListItemIcon>
                    <ListItemText
                      primary={
                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography component="span" variant="subtitle2" sx={{ fontWeight: 600, fontFamily: 'var(--font-mono)' }}>
                            {notification.title}
                          </Typography>
                          {!notification.read && (
                            <Chip label="New" size="small" color="primary" sx={{ height: 20 }} />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box component="span" sx={{ display: 'block' }}>
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{ display: 'block', color: 'text.primary', whiteSpace: 'pre-wrap', mt: 0.5 }}
                          >
                            {notification.body}
                          </Typography>
                          <Typography component="span" variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                            {formatDistanceToNow(new Date(notification.sentAt), { addSuffix: true })}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < notifications.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </Box>

      {/* Snackbar for feedback messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </SwipeableDrawer>
  );
};

export default NotificationCenter;
